import { useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Section } from 'ui/molecules/Section/Section';

import styles from './CompanyDataDelivery.module.scss';

type RouteKey = 'mcp' | 'rest' | 'regis' | 'bulk' | 'dict';
type FilterKey = 'all' | 'mcp' | 'rest' | 'regis' | 'bulk';

type DeliveryRow = {
  id: string;
  route: RouteKey;
  href: string;
  chip: string;
  question: string;
  why: string;
};

type Translate = (key: string) => string;

const getFilters = (t: Translate): { key: FilterKey; label: string }[] => [
  { key: 'all', label: t('All') },
  { key: 'mcp', label: t('MCP server') },
  { key: 'rest', label: t('REST API') },
  { key: 'regis', label: t('Regis API') },
  { key: 'bulk', label: t('Bulk feed') },
];

const getRows = (t: Translate): DeliveryRow[] => [
  {
    id: 'mcp-client',
    route: 'mcp',
    href: '/mcp-integration',
    chip: t('MCP server'),
    question: t('It runs in Claude, OpenAI, Microsoft Copilot or another MCP client'),
    why: t(
      'Connectors for Claude, OpenAI and Microsoft Copilot. Connect once and the dataset appears as callable tools: resolve, verify, shareholders, UBOs, group structure, financials. No code in the agent itself.',
    ),
  },
  {
    id: 'rest-field',
    route: 'rest',
    href: '/api',
    chip: t('REST API'),
    question: t('It is a pipeline that already knows the field it needs'),
    why: t(
      'Fixed schemas, one endpoint per dataset, an include_provenance flag on the request. Token in the header, no SDK.',
    ),
  },
  {
    id: 'regis-question',
    route: 'regis',
    href: '/regis-api',
    chip: t('Regis API'),
    question: t('Its input is a question, not a field name'),
    why: t(
      'One string in. Entities resolved, records retrieved, a source on every value, a stated gap where the register has none.',
    ),
  },
  {
    id: 'bulk-index',
    route: 'bulk',
    href: '/book-a-demo',
    chip: t('Bulk feed'),
    question: t('You index company data into your own retrieval or vector layer'),
    why: t('Scheduled, daily-synced extracts with provenance carried per record. Redistribution under licence.'),
  },
  {
    id: 'rest-kyb',
    route: 'rest',
    href: '/api',
    chip: t('REST API'),
    question: t('It onboards customers, merchants or sellers and needs KYB before approval'),
    why: t(
      'The KYB verification endpoint returns identity, status and officers from the statutory register, so the approval decision carries a filing, not a guess.',
    ),
  },
  {
    id: 'regis-chain',
    route: 'regis',
    href: '/regis-api',
    chip: t('Regis API'),
    question: t('It walks ownership chains for AML or sanctions work'),
    why: t(
      'Ask for the full chain in one question. Each hop comes back against the register that recorded it, ready for the case file.',
    ),
  },
  {
    id: 'rest-financials',
    route: 'rest',
    href: '/api',
    chip: t('REST API'),
    question: t('It reads financial statements for underwriting decisions'),
    why: t('Filed accounts as structured fields by period, with the document reference attached to the extraction.'),
  },
  {
    id: 'mcp-procurement',
    route: 'mcp',
    href: '/mcp-integration',
    chip: t('MCP server'),
    question: t('It verifies suppliers or bidders before a purchase order goes out'),
    why: t(
      'A procurement copilot calls the verification tools directly: registered, active, owned by whom it declares, each answer sourced.',
    ),
  },
  {
    id: 'bulk-monitor',
    route: 'bulk',
    href: '/book-a-demo',
    chip: t('Bulk feed'),
    question: t('It monitors a portfolio for status, ownership or filing changes'),
    why: t(
      'Daily-synced extracts diffed on your side, so every change your agent flags traces to the register that published it.',
    ),
  },
  {
    id: 'dict-check',
    route: 'dict',
    href: '/data-dictionary',
    chip: t('Data dictionary'),
    question: t('You want to check the data before any of this'),
    why: t('Every field, dataset by dataset, with its definition. No key needed.'),
  },
];

export const CompanyDataDelivery = () => {
  const { t } = useInt();
  const [filter, setFilter] = useState<FilterKey>('all');

  const FILTERS = getFilters(t);
  const ROWS = getRows(t);

  const visibleRows = ROWS.filter((row) => filter === 'all' || row.route === filter || row.route === 'dict');

  return (
    <Section
      className={styles.root}
      title={t('MCP server, Regis, bulk feed and API. One dataset behind all four.')}
      description={t(
        'The same registry records sit behind every route. Find the row that sounds like your agent; the route on the right is the one to take.',
      )}
    >
      <div className={styles.glowBlue} aria-hidden="true" />
      <div className={styles.glowIndigo} aria-hidden="true" />

      <div className={styles.refWrap}>
        <Button type="dark-outline" size="lg" rounded="full" href="https://api.globaldatabase.com/docs/v2">
          {t('Read the API reference')}
        </Button>
      </div>

      <div className={styles.chooser}>
        <div className={styles.chooserHead}>
          <span className={styles.chooserLabel}>{t('Which route fits your agent')}</span>

          <div className={styles.filters} role="group" aria-label="Filter by route">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={cn(styles.filterBtn, filter === f.key && styles.filterBtnActive)}
                aria-pressed={filter === f.key}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div key={filter} className={styles.rows}>
          {visibleRows.map((row) => (
            <a key={row.id} className={styles.row} href={row.href}>
              <span className={styles.rowIf}>{row.question}</span>
              <span className={styles.rowGo}>
                {row.chip}
                <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                  <path d="M2 6h7M6 2.5 9.5 6 6 9.5" />
                </svg>
              </span>
              <span className={styles.rowWhy}>{row.why}</span>
            </a>
          ))}
        </div>
      </div>
    </Section>
  );
};
